import { getSession, setSession, trpc } from '@tg/app-core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, shortDate } from '../lib/dates';
import { forgetPush } from '../lib/push';
import { phoneShow, useMe, useToday } from '../lib/data';
import { clearCache } from '../lib/query';
import { lsClear } from '../lib/storage';
import { THEMES, applyTheme, getTheme, type Theme } from '../lib/theme';
import { visibleBlocks } from '../lib/workout';
import { I } from '../ui/icons';
import { Ask, toast } from '../ui/overlay';
import { Tabs, Top } from '../ui/parts';
import { OFFLINE_AHEAD, useDaysRange } from './Home';

/* P1. Настройки — только то, что клиент решает сам: уведомления, тема,
   «не гасить экран», что лежит на устройстве (CLI-3), аккаунт, документы. */
const NOTIF: [string, string][] = [
  ['plan', 'Новая тренировка от тренера'],
  ['msg', 'Сообщения тренера'],
  ['remind', 'Напоминание о тренировке'],
];

/** Выход: сессия, кэш и всё своё на устройстве; тема остаётся */
export function signOut() {
  setSession(null);
  clearCache();
  lsClear(['theme', 'installShown']);
}

export function Settings() {
  const nav = useNavigate();
  const me = useMe();
  const today = useToday();
  const utils = trpc.useUtils();
  const save = trpc.clientApp.settings.useMutation({ onSuccess: () => utils.clientApp.me.invalidate() });
  const logout = trpc.auth.logout.useMutation();
  const del = trpc.clientApp.deleteAccount.useMutation();
  const [theme, setTheme] = useState<Theme>(getTheme());
  const [ask, setAsk] = useState(false);
  const offline = useDaysRange(today, addDays(today, OFFLINE_AHEAD));

  const st = (me.data?.settings || {}) as Record<string, unknown>;
  const flag = (k: string) => st[k] !== false;
  const toggle = (k: string) => {
    const v = !flag(k);
    utils.clientApp.me.setData(undefined, (x) => (x ? { ...x, settings: { ...(x.settings || {}), [k]: v } } : x));
    save.mutate({ [k]: v });
  };
  const onTheme = (t: Theme) => {
    setTheme(t);
    applyTheme(t);
    save.mutate({ theme: t });
  };
  /* Докуда тренировки уже на устройстве: последний день с тренировкой в оффлайн-кэше */
  const till = [...offline.map.entries()].filter(([, c]) => visibleBlocks(c).length).map(([d]) => d).sort().pop();

  const out = async () => {
    await forgetPush();
    const s = getSession();
    if (s) logout.mutate({ token: s.refresh });
    signOut();
    nav('/phone', { replace: true });
  };
  const remove = async () => {
    try {
      await del.mutateAsync();
      setAsk(false);
      signOut();
      nav('/phone', { replace: true });
    } catch {
      toast('Не получилось удалить — проверьте связь');
    }
  };
  const sw = (k: string, label: string) => (
    <button key={k} className="srow" role="switch" aria-checked={flag(k)} onClick={() => toggle(k)}>
      <span>{label}</span>
      <i className="tgl" />
    </button>
  );
  const doc = (label: string) => (
    <button className="srow" onClick={() => toast('Документ откроется в браузере')}>
      <span>{label}</span>
      <I n="right" />
    </button>
  );
  return (
    <>
      <Top title="Настройки" />
      <div className="pg sets">
        <p className="lbl gcap">Уведомления</p>
        <div className="glist">{NOTIF.map(([k, n]) => sw(k, n))}</div>
        <p className="lbl gcap">Оформление</p>
        <div className="glist">
          <label className="srow sel">
            <span>Тема</span>
            <select aria-label="Тема" value={theme} onChange={(e) => onTheme(e.target.value as Theme)}>
              {THEMES.map(([k, n]) => (
                <option key={k} value={k}>
                  {n}
                </option>
              ))}
            </select>
            <I n="down" />
          </label>
        </div>
        <p className="lbl gcap">Тренировка</p>
        <div className="glist">
          {sw('awake', 'Не гасить экран')}
          <div className="srow">
            <span>Доступно без интернета</span>
            <s>{till ? 'до ' + shortDate(till, today) : 'нет тренировок'}</s>
          </div>
        </div>
        <p className="lbl gcap">Аккаунт</p>
        <div className="glist">
          <div className="srow">
            <span>Телефон</span>
            <s>{phoneShow(me.data?.phone)}</s>
          </div>
          <button className="srow" onClick={out}>
            <span>Выйти</span>
          </button>
          <button className="srow rm" onClick={() => setAsk(true)}>
            <span>Удалить аккаунт</span>
          </button>
        </div>
        <p className="lbl gcap">О приложении</p>
        <div className="glist">
          {doc('Политика конфиденциальности')}
          {doc('Пользовательское соглашение')}
          <div className="srow">
            <span>Версия</span>
            <s>{__APP_VERSION__}</s>
          </div>
        </div>
      </div>
      <Tabs on="profile" />
      <Ask
        open={ask}
        onClose={() => setAsk(false)}
        title="Удалить аккаунт?"
        text="Вход по этому номеру, фото, рекорды и замеры удалятся без восстановления. История тренировок и результатов останется у тренера."
        label="Удалить аккаунт"
        onOk={remove}
      />
    </>
  );
}
